import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Footer from '../components/Footer'
import SEO from '../components/SEO'
import SiteNav from '../components/SiteNav'
import { blogApi, type BlogPostSummary } from '../services/api'
import '../styles/blog.css'

const PAGE_TITLE = 'OpenDeploy Blog'
const PAGE_DESCRIPTION =
  'Practical guides for deploying apps from coding agents — Claude Code, Cursor, OpenAI Codex, OpenClaw — across Next.js, Vite, FastAPI, Express, and more.'

function buildJsonLd(posts: BlogPostSummary[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': 'https://opendeploy.dev/blog#blog',
    url: 'https://opendeploy.dev/blog',
    name: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    inLanguage: 'en',
    publisher: { '@id': 'https://opendeploy.dev/#organization' },
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      '@id': `https://opendeploy.dev/blog/${post.slug}#post`,
      headline: post.title,
      description: post.description,
      url: `https://opendeploy.dev/blog/${post.slug}`,
      datePublished: post.published_at,
      inLanguage: 'en',
      author: { '@id': 'https://opendeploy.dev/#organization' },
      publisher: { '@id': 'https://opendeploy.dev/#organization' },
    })),
  }
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d="m21 21-4.3-4.3M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14Z"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.8}
      />
    </svg>
  )
}

function formatDate(iso: string) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

function matches(post: BlogPostSummary, q: string) {
  return [post.title, post.description, post.category, post.tldr, post.tags.join(' ')]
    .join(' ')
    .toLowerCase()
    .includes(q)
}

function PostCard({ post, featured }: { post: BlogPostSummary; featured?: boolean }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`od-blog-card${featured ? ' od-blog-card--featured' : ''}`}
    >
      {post.cover_image_url && (
        <div className="od-blog-card-cover">
          <img src={post.cover_image_url} alt="" loading="lazy" />
        </div>
      )}
      <div className="od-blog-card-body">
        <div className="od-blog-card-meta">
          <span className="od-blog-card-category">{post.category}</span>
          {post.published_at && (
            <>
              <span aria-hidden="true">·</span>
              <span>{formatDate(post.published_at)}</span>
            </>
          )}
          {post.read_time && (
            <>
              <span aria-hidden="true">·</span>
              <span>{post.read_time}</span>
            </>
          )}
        </div>
        <h3>{post.title}</h3>
        <p>{post.description}</p>
        <div className="od-blog-card-foot">
          <div className="od-blog-card-tags">
            {post.tags.slice(0, 3).map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          {post.comment_count > 0 && (
            <span className="od-blog-card-comments">
              {post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}

export default function Blog() {
  const [posts, setPosts] = useState<BlogPostSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    let active = true
    blogApi
      .listPosts()
      .then((res) => {
        if (active) setPosts(res.data || [])
      })
      .catch(() => {
        if (active) setError('Could not load posts. Please try again later.')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return posts
    return posts.filter((p) => matches(p, q))
  }, [posts, query])

  const [featured, ...rest] = visible

  return (
    <>
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESCRIPTION}
        path="/blog"
        jsonLd={buildJsonLd(posts)}
      />

      <div className="od-blog-page od-blog-light">
        <SiteNav />
        <div className="od-blog-frame">
          <header className="od-blog-hero">
            <p className="od-blog-kicker">Blog</p>
            <h1>How to deploy from your coding agent</h1>
            <p>
              Plain-English guides for shipping real apps from Claude Code, Cursor,
              OpenAI Codex, and OpenClaw — across Next.js, Vite, FastAPI, Express, and
              more. Written for builders who let the agent do the deploy.
            </p>

            <label className="od-blog-search">
              <SearchIcon />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search posts — claude, next.js, postgres, fastapi..."
                aria-label="Search blog posts"
              />
            </label>
          </header>

          <section className="od-blog-listing" aria-label="Blog posts">
            {loading ? (
              <p className="od-blog-state">Loading posts…</p>
            ) : error ? (
              <p className="od-blog-state">{error}</p>
            ) : visible.length === 0 ? (
              <p className="od-blog-state">
                {query.trim() ? 'No posts match that search.' : 'No posts yet — check back soon.'}
              </p>
            ) : (
              <>
                {featured && !query.trim() && <PostCard post={featured} featured />}
                <div className="od-blog-grid">
                  {(query.trim() ? visible : rest).map((post) => (
                    <PostCard key={post.slug} post={post} />
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
        <Footer />
      </div>
    </>
  )
}
